import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PiArrowSquareOut, PiLightning } from 'react-icons/pi';
import { ApiError } from '@/lib/api';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import { holds } from '@/lib/permissionMatch';
import {
  invalidateDocumentQueries,
  type TDocumentGroup,
  type TGroupProforma,
  useCreateOpportunity,
} from '@/models/invoices';
import { usePermissions } from '@/models/permissions';

/** Every proforma of the order: outside a plan, in the plan in force and in earlier plans. */
const allProformas = (g: TDocumentGroup): TGroupProforma[] => [
  ...g.proformas,
  ...(g.plan?.items.flatMap((i) => i.proformas) ?? []),
  ...g.pastPlans.flatMap((p) => p.items.flatMap((i) => i.proformas)),
];

/**
 * The offer's one Monday opportunity, in the page header: nothing until the order has its first
 * real invoice (a Settled proforma); then Create Opportunity, made from a Settled proforma - this
 * page's when it is one - and once it exists, Open Opportunity.
 */
export function OpportunityButton({
  group,
}: {
  group: TDocumentGroup | undefined;
}) {
  const { permissions } = usePermissions();
  const create = useCreateOpportunity();
  const offer = group?.offer;
  if (!group || !offer || group.excluded === 'renewal') return null;

  const monday = offer.monday;
  if (monday.itemId != null) {
    return (
      <Button
        size="xs"
        variant="light"
        component="a"
        href={mondayItemUrl(MONDAY_BOARDS.OPPORTUNITIES, monday.itemId)}
        target="_blank"
        rel="noreferrer"
        leftSection={<PiArrowSquareOut size={14} />}
      >
        Open Opportunity
      </Button>
    );
  }

  const settled = allProformas(group).filter((p) => p.status === 'paid');
  if (settled.length === 0) return null;
  if (!holds(permissions, 'invoices:update-status')) return null;
  const source =
    settled.find((p) => p.current || p.finalInvoice?.current) ?? settled[0];

  const doCreate = () =>
    create.mutate(source.id, {
      onSuccess: () => invalidateDocumentQueries(),
      onError: (e) =>
        notifications.show({
          color: 'red',
          title: 'Opportunity not created',
          message: e instanceof ApiError ? e.body : String(e),
        }),
    });

  return (
    <Button
      size="xs"
      leftSection={<PiLightning size={14} />}
      loading={create.isPending || monday.state === 'creating'}
      color={monday.state === 'failed' ? 'orange' : undefined}
      onClick={doCreate}
    >
      {monday.state === 'failed' ? 'Retry Opportunity' : 'Create Opportunity'}
    </Button>
  );
}
